'use client';

import Image from 'next/image'; // ALLOW-RAW-IMG: OutcomePill is a generic primitive — outcome avatar URLs come from arbitrary origins (data:, IPFS, external CDNs, static `/team/*`). NSFW gating + entity lifecycle is the caller's concern; threading <OptimizedImage>'s entity-shaped props would couple the primitive to user-content moderation logic that doesn't belong here.
import type { Context, MouseEvent, ReactNode } from 'react';
import { createContext, use } from 'react';

import { useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import type { OutcomeHueName } from '@/lib/chart/outcome-hue';
import { cn } from '@/lib/ui/utils';
import { useLightboxStore } from '@/stores/lightbox';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type OutcomePillSize = 'xs' | 'sm' | 'md' | 'lg';

/**
 * `compact` — cap label width + ellipsis (dense rows, horizontal scroll).
 * `natural` — label uses available width and wraps; use in flex-wrap stacks (e.g. activity feed).
 */
export type OutcomePillLabelLayout = 'compact' | 'natural';

export interface OutcomePillProps {
  /** Explicit outcome hue. Mutually exclusive with `outcome`. */
  hue?: OutcomeHueName | undefined;
  /** Outcome identity. Hue derived via `useOutcomeColor` (Yes/No reserved). */
  outcome?: { title: string; index: number } | undefined;
  /** Size scale. Default `'xs'` (Figma 677:621 reference). */
  size?: OutcomePillSize | undefined;
  /** Image URL rendered as a 20px avatar circle. Only honored at `size="md"`. */
  image?: string | undefined;
  /** Alt text for `image`. Defaults to empty string (decorative — label carries semantics). */
  imageAlt?: string | undefined;
  /** When true, clicking the image opens it in the global lightbox. Only honored when `image` is set. */
  imageClickable?: boolean | undefined;
  /** Flat convenience: rendered when no `children`. */
  label?: ReactNode | undefined;
  /** Flat convenience: rendered when no `children`. */
  value?: ReactNode | undefined;
  /** How the label slot handles long text. Default `compact`. */
  labelLayout?: OutcomePillLabelLayout | undefined;
  /** Compound form. When set, wins over flat `label`/`value`. */
  children?: ReactNode | undefined;
  /** Layout passthrough; merged via cn() after intrinsic root classes. */
  className?: string | undefined;
}

export interface OutcomePillDotProps {
  className?: string | undefined;
}

export interface OutcomePillLabelProps {
  children?: ReactNode | undefined;
  className?: string | undefined;
}

export interface OutcomePillValueProps {
  children?: ReactNode | undefined;
  className?: string | undefined;
}

export interface OutcomePillImageProps {
  src: string;
  alt?: string | undefined;
  className?: string | undefined;
  /** When provided, the image renders as a button that fires this handler on click. */
  onClick?: (() => void) | undefined;
}

// ---------------------------------------------------------------------------
// Token tables (module-level, React Compiler friendly)
// ---------------------------------------------------------------------------

const ROOT_BASE = 'inline-flex items-center rounded-pill select-none cursor-default' as const;

// `md` vertical padding is `py-[3px]` for both branches (aligned with `StatusPill`).
// With-image uses asymmetric `pl-[3px] pr-2`; without-image uses symmetric `px-3`.
const ROOT_SIZE: Record<OutcomePillSize, string> = {
  xs: 'gap-1 px-1.5 py-1',
  sm: 'gap-1.5 px-2 py-1.5',
  md: 'gap-1.5 px-3 py-[3px]',
  lg: 'gap-2 px-3.5 py-1.5',
};

const ROOT_SIZE_MD_WITH_IMAGE = 'gap-1.5 pl-[3px] pr-2 py-[3px]' as const;
// At `lg` the image is 28px so the left padding shrinks proportionally to
// keep the avatar visually flush with the pill's rounded edge.
const ROOT_SIZE_LG_WITH_IMAGE = 'gap-2 pl-1 pr-3 py-1' as const;

const DOT_SIZE: Record<OutcomePillSize, string> = {
  xs: 'size-1.5 shrink-0 rounded-full',
  sm: 'size-2 shrink-0 rounded-full',
  md: 'size-2 shrink-0 rounded-full',
  lg: 'size-2.5 shrink-0 rounded-full',
};

const TEXT_SIZE: Record<OutcomePillSize, string> = {
  xs: 'text-micro leading-[10px] font-medium',
  sm: 'text-caption leading-tight font-medium',
  md: 'text-caption leading-tight font-medium tracking-[-0.14px]', // ARBITRARY: tracking matches Figma 3333:2398
  lg: 'text-body leading-tight font-medium',
};

const TEXT_NOWRAP = 'whitespace-nowrap' as const;

// Per-size cap on the LABEL slot only — long outcome names truncate with `…`
// so the value (percent) stays whole and the pill stays compact in
// horizontal-scroll containers. Pixel values are out-of-scale because the
// pill's text is also out-of-scale (10px / 12px) — Tailwind spacing tokens
// don't have a matching cap that pairs with these text sizes.
const LABEL_MAX_WIDTH: Record<OutcomePillSize, string> = {
  xs: 'max-w-[40px] truncate', // ARBITRARY: tight cap at 10px text — keeps the pill compact in horizontal-scroll rows; long outcome names ellipsis-cut
  sm: 'max-w-[56px] truncate', // ARBITRARY: same tight-cap rationale at sm size
  md: 'max-w-[120px] truncate', // ARBITRARY: wider cap for body-text labels (proper nouns like "Pep Guardiola")
  lg: 'max-w-[160px] truncate', // ARBITRARY: wider cap for the 14px lg pill used in the proposal-outcomes panel — keeps multi-word labels readable
};

// Image-variant sizes for the leading avatar. Only `md` and `lg` allow
// images; the wrapper class adjusts to match `--avatar-px`.
const IMAGE_SIZE_PX: Record<'md' | 'lg', number> = { md: 20, lg: 28 };
const IMAGE_WRAPPER_SIZE: Record<'md' | 'lg', string> = { md: 'size-5', lg: 'size-7' };

const VALUE_EXTRA = 'font-mono tabular-nums' as const;

// ---------------------------------------------------------------------------
// Internal context — carries hue + size from root to subcomponents
// ---------------------------------------------------------------------------

interface OutcomePillContextValue {
  hue: OutcomeHueName;
  size: OutcomePillSize;
  labelLayout: OutcomePillLabelLayout;
}

const OutcomePillContext: Context<OutcomePillContextValue | null> =
  createContext<OutcomePillContextValue | null>(null);

function useOutcomePillContext(): OutcomePillContextValue {
  const ctx = use(OutcomePillContext);
  if (ctx === null) {
    throw new Error('OutcomePill subcomponents must be rendered inside <OutcomePill>');
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// Subcomponents (named exports + dot-namespace sugar attached at module bottom)
// ---------------------------------------------------------------------------

export function OutcomePillDot({ className }: OutcomePillDotProps): React.ReactNode {
  const { hue, size } = useOutcomePillContext();
  // Dot uses `-text` (not `-cta`) so it matches the label color and the
  // avatar border for visual coherence — at small sizes `-cta` reads as
  // washed-out vibrant on the light `-outline` pill bg, especially in
  // light mode for warm hues (yellow, lime, amber). Same token everywhere
  // makes the pill chrome read as one ink color.
  return (
    <span aria-hidden="true" className={cn(DOT_SIZE[size], `bg-outcome-${hue}-text`, className)} />
  );
}

export function OutcomePillLabel({ children, className }: OutcomePillLabelProps): React.ReactNode {
  const { hue, size, labelLayout } = useOutcomePillContext();
  const flowClass =
    labelLayout === 'natural'
      ? 'min-w-0 max-w-full whitespace-normal break-words [overflow-wrap:anywhere]'
      : cn(TEXT_NOWRAP, LABEL_MAX_WIDTH[size]);
  return (
    <span className={cn(TEXT_SIZE[size], flowClass, `text-outcome-${hue}-text`, className)}>
      {children}
    </span>
  );
}

export function OutcomePillValue({ children, className }: OutcomePillValueProps): React.ReactNode {
  const { hue, size } = useOutcomePillContext();
  return (
    <span
      className={cn(
        TEXT_SIZE[size],
        TEXT_NOWRAP,
        VALUE_EXTRA,
        `text-outcome-${hue}-text`,
        className,
      )}
    >
      {children}
    </span>
  );
}

export function OutcomePillImage({
  src,
  alt,
  className,
  onClick,
}: OutcomePillImageProps): React.ReactNode {
  const { hue, size } = useOutcomePillContext();
  if (size !== 'md' && size !== 'lg') {
    throw new Error('OutcomePill.Image: only supported at size="md" or "lg"');
  }
  const imagePx = IMAGE_SIZE_PX[size];
  const wrapperClass = cn(
    // ARBITRARY: 1.2px border between standard 1px and 2px per Figma 3333:2398
    IMAGE_WRAPPER_SIZE[size],
    'shrink-0 overflow-hidden rounded-full border-[1.2px]',
    `bg-outcome-${hue}-cta`,
    // `border-outcome-${hue}-text` is enumerated literally in
    // `OUTCOME_BORDER_TEXT_CLASS_SAFELIST` so Tailwind v4's scanner emits
    // the per-hue rule. Without that safelist entry, the border-color
    // utility wouldn't generate and would fall back to the global
    // `--border-secondary` shim in `globals.css`.
    `border-outcome-${hue}-text`,
    onClick !== undefined && 'cursor-zoom-in transition-opacity hover:opacity-80',
    className,
  );
  const inner = (
    <Image
      alt={alt ?? ''}
      className="size-full object-cover"
      height={imagePx}
      src={src}
      // `unoptimized` so callers can pass any URL (data:, external, SVG, IPFS,
      // static `/team/*.svg`) without configuring `next.config.ts` remotePatterns
      // for every origin. The small (20–28 px) avatar is too small to benefit
      // from Next.js' optimizer pipeline anyway.
      unoptimized
      width={imagePx}
    />
  );
  if (onClick !== undefined) {
    return (
      <button
        type="button"
        onClick={(e: MouseEvent<HTMLButtonElement>): void => {
          e.preventDefault();
          e.stopPropagation();
          onClick();
        }}
        aria-label={alt !== undefined && alt !== '' ? alt : undefined}
        className={wrapperClass}
      >
        {inner}
      </button>
    );
  }
  return (
    <span
      aria-hidden={alt === undefined || alt === '' ? 'true' : undefined}
      className={wrapperClass}
    >
      {inner}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Root component
// ---------------------------------------------------------------------------

const FALLBACK_OUTCOME = { title: '', index: 0 } as const;

function OutcomePillImpl({
  hue,
  outcome,
  size = 'xs',
  image,
  imageAlt,
  imageClickable,
  label,
  value,
  labelLayout = 'compact',
  children,
  className,
}: OutcomePillProps): React.ReactNode {
  // Hooks must be called unconditionally — pass a stable fallback when
  // `outcome` is not provided. The derived hue is only consumed when the
  // caller did not pass `hue` explicitly.
  const derived = useOutcomeColor(outcome ?? FALLBACK_OUTCOME);

  if (hue === undefined && outcome === undefined) {
    throw new Error('OutcomePill: requires either `hue` or `outcome` prop');
  }

  if (image !== undefined && size !== 'md' && size !== 'lg') {
    throw new Error('OutcomePill: `image` is only supported at size="md" or "lg"');
  }

  const resolvedHue: OutcomeHueName = hue ?? derived.hue;
  const hasImage = image !== undefined && (size === 'md' || size === 'lg');
  const rootSizeClasses = hasImage
    ? size === 'lg'
      ? ROOT_SIZE_LG_WITH_IMAGE
      : ROOT_SIZE_MD_WITH_IMAGE
    : ROOT_SIZE[size];

  const leading = hasImage ? (
    <OutcomePillImage
      src={image}
      alt={imageAlt}
      onClick={
        imageClickable === true
          ? (): void => {
              useLightboxStore.getState().openLightbox([{ src: image, alt: imageAlt ?? '' }]);
            }
          : undefined
      }
    />
  ) : (
    <OutcomePillDot />
  );

  const content =
    children !== undefined ? (
      children
    ) : (
      <>
        {leading}
        {label !== undefined ? <OutcomePillLabel>{label}</OutcomePillLabel> : null}
        {value !== undefined ? <OutcomePillValue>{value}</OutcomePillValue> : null}
      </>
    );

  return (
    <OutcomePillContext.Provider value={{ hue: resolvedHue, size, labelLayout }}>
      <span
        className={cn(
          ROOT_BASE,
          labelLayout === 'natural' && 'max-w-full items-start',
          rootSizeClasses,
          `bg-outcome-${resolvedHue}-outline`,
          className,
        )}
      >
        {content}
      </span>
    </OutcomePillContext.Provider>
  );
}

// Dot-namespace sugar — `<OutcomePill.Dot />` works alongside named imports.
interface OutcomePillType {
  (props: OutcomePillProps): React.ReactNode;
  Dot: typeof OutcomePillDot;
  Image: typeof OutcomePillImage;
  Label: typeof OutcomePillLabel;
  Value: typeof OutcomePillValue;
}

export const OutcomePill: OutcomePillType = Object.assign(OutcomePillImpl, {
  Dot: OutcomePillDot,
  Image: OutcomePillImage,
  Label: OutcomePillLabel,
  Value: OutcomePillValue,
});
